import React, { PureComponent } from 'react';
import { readStoryStream, loadMoreNews } from './actions';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { getSite } from '../global/reducers';
import {
  loadFollowingList,
  clearFollowingList,
} from 'modules/following/actions';
import {
  getStoryStream,
  getStoryStreamStatus,
  getNewsLoadMoreLink,
} from './reducers';
import { STATES, SITES } from 'lib/constants';
import { getLocale, isAuthenticated } from 'modules/user/reducers';
import CloudinayImage from 'components/CloudinaryImage';
import Loading from 'components/Loading';
import Teaser from 'components/Teaser';
import Button from '@mynewsdesk/ui/lib/Button';
import './_styles.scss';

export const getColumnIndicies = itemHeights => {
  let leftColumnHeight = 0;
  let rightColumnHeight = 0;
  let leftIndicies = [];
  let rightIndicies = [];

  itemHeights.forEach((height, index) => {
    if (leftColumnHeight <= rightColumnHeight) {
      leftColumnHeight += height;
      leftIndicies.push(index);
    } else {
      rightColumnHeight += height;
      rightIndicies.push(index);
    }
  });

  return [leftIndicies, rightIndicies];
};

class Start extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      storiesList: props.stories || [],
      itemHeights: [],
      leftIndicies: [],
      rightIndicies: [],
    };
    this.refStoryStream = React.createRef();
  }

  componentDidMount() {
    const {
      readStoryStream,
      isAuthenticated,
      getWhoIamFollowing,
      clearFollowings,
    } = this.props;
    readStoryStream();
    isAuthenticated ? getWhoIamFollowing() : clearFollowings();
  }

  componentDidUpdate(prevProps) {
    const { isAuthenticated, getWhoIamFollowing, clearFollowings } = this.props;
    if (isAuthenticated !== prevProps.isAuthenticated) {
      isAuthenticated ? getWhoIamFollowing() : clearFollowings();
    }
    if (
      prevProps.stories !== this.props.stories ||
      prevProps.isAuthenticated !== this.props.isAuthenticated
    ) {
      let storiesList = [...this.props.stories];
      let leftIndicies = this.state.leftIndicies || [];
      let rightIndicies = this.state.rightIndicies || [];

      storiesList.forEach((item, index) => {
        if (!leftIndicies.includes(index) && !rightIndicies.includes(index)) {
          if (index % 2 === 0) {
            leftIndicies.push(index);
          } else {
            rightIndicies.push(index);
          }
        }
      });

      this.setState({
        storiesList,
        leftIndicies: leftIndicies,
        rightIndicies: rightIndicies,
      });
    }

    this.layoutColumns();
  }

  layoutColumns = () => {
    if (this.refStoryStream.current) {
      let itemHeights = Array.from(
        this.refStoryStream.current.getElementsByClassName(
          'mnd-story-stream__item',
        ),
      )
        .sort((a, b) => a.dataset.index - b.dataset.index)
        .map(el => el.getBoundingClientRect().height);

      if (
        this.state.itemHeights.length !== itemHeights.length ||
        !this.state.itemHeights.every(
          (value, index) => value === itemHeights[index],
        )
      ) {
        let [leftIndicies, rightIndicies] = getColumnIndicies(itemHeights);

        this.setState({
          itemHeights: itemHeights,
          leftIndicies: leftIndicies,
          rightIndicies: rightIndicies,
        });
      }
    }
  };

  getStory = (
    {
      header,
      imageCloudinaryId,
      imageCropping,
      imageUrl,
      pressroomLogotypeUrl,
      pressroomName,
      brandAvatarUrl,
      publicUrl,
      publishedAt,
      summary,
      id,
      pressroomId,
      locale,
      imageCaption,
    },
    index,
    userLocale,
  ) => {
    return (
      <Teaser
        className="mnd-story-stream__item"
        heading={header}
        image={
          imageCloudinaryId && (
            <CloudinayImage
              cloudinaryId={imageCloudinaryId}
              cropping={imageCropping}
              sizes={'(max-width: 720px) 100vw, (max-width: 992px) 50vw, 460px'}
              alt={imageCaption || 'Cover image'}
            />
          )
        }
        key={id}
        id={id}
        pressroomId={pressroomId}
        logoUrl={brandAvatarUrl || pressroomLogotypeUrl}
        name={pressroomName}
        summary={summary}
        time={publishedAt}
        url={publicUrl}
        index={index}
        userLocale={userLocale}
        contentLocale={locale}
      />
    );
  };

  render() {
    const {
      storiesStatus,
      loadMoreLink,
      onLoadMoreNews,
      t = s => s,
    } = this.props;
    const userLocale = this.props.locale;
    const { storiesList } = this.state;
    return (
      <div className="mnd-column-full reader-main mnd-column-grow">
        {storiesList.length > 0 && (
          <div
            className="mnd-story-stream"
            ref={this.refStoryStream}
            onLoad={this.layoutColumns}
          >
            <div className="mnd-story-stream_column">
              {this.state.leftIndicies.map(index =>
                this.getStory(storiesList[index], index, userLocale),
              )}
            </div>
            <div className="mnd-story-stream_column">
              {this.state.rightIndicies.map(index =>
                this.getStory(storiesList[index], index, userLocale),
              )}
            </div>
          </div>
        )}
        {storiesStatus === STATES.FAILURE && (
          <h1>{t('start_story-stream_state-failure-heading')}</h1>
        )}
        {storiesStatus === STATES.LOADING && <Loading delay={250} />}
        {loadMoreLink && storiesStatus !== STATES.FAILURE && (
          <div className="mnd-search__button-wrapper">
            <Button
              bsStyle="primary"
              className="mnd-search__action--primary mnd-button"
              onClick={onLoadMoreNews}
            >
              {t('start_story-stream_load-more-button')}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default connect(
  state => ({
    locale: getLocale(state),
    site: getSite(state) || SITES['com'],
    stories: getStoryStream(state),
    storiesStatus: getStoryStreamStatus(state),
    isAuthenticated: isAuthenticated(state),
    loadMoreLink: getNewsLoadMoreLink(state),
  }),
  (dispatch, ownProps) => ({
    _readStoryStream: siteCode => dispatch(readStoryStream(siteCode)),
    getWhoIamFollowing: () => dispatch(loadFollowingList()),
    _onLoadMoreNews: link => dispatch(loadMoreNews(link)),
    clearFollowings: () => dispatch(clearFollowingList()),
  }),
  (
    { ...stateProps },
    { _readStoryStream, _toggleFollow, _onLoadMoreNews, ...dispatchProps },
    ownProps,
  ) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    readStoryStream: () => _readStoryStream(stateProps.site.code),
    onLoadMoreNews: link => _onLoadMoreNews(stateProps.loadMoreLink),
  }),
)(withNamespaces()(Start));
